import { useEffect, useRef, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { accessTokenRequiresPasswordChange, useAuthStore } from '@/store/authStore';
import { apiClient, refreshAccessToken } from '@/lib/apiClient';
import { parsePlatformFeatures } from '@/api/auth.api';

export interface LoginRedirectState {
    from: string;
    reason?: 'expired';
}

/** UX-10: keep the full return path (path + query + hash) and say why we left. */
export function loginRedirectState(
    location: { pathname: string; search?: string; hash?: string },
    sessionEndReason: 'expired' | null,
): LoginRedirectState {
    const from = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
    return sessionEndReason === 'expired' ? { from, reason: 'expired' } : { from };
}

export const CHANGE_PASSWORD_ROUTE = '/change-password';

/** BE-07: while a password change is required, every other protected page redirects. */
export function passwordChangeRedirect(pathname: string, required: boolean): string | null {
    return required && pathname !== CHANGE_PASSWORD_ROUTE ? CHANGE_PASSWORD_ROUTE : null;
}

/**
 * Route guard.
 *
 * If an in-memory access token is present we render. Otherwise we try
 * a silent refresh against the httpOnly cookie. If refresh succeeds,
 * /auth/me rehydrates the user.
 */
export function ProtectedRoute({ children }: { children: ReactElement }): ReactElement {
    const { t } = useTranslation();
    const { accessToken, user, setSession, setUser, reset, sessionEndReason } = useAuthStore();
    const [rehydrating, setRehydrating] = useState(!accessToken || !user);
    const [ok, setOk] = useState(Boolean(accessToken && user));
    const [slow, setSlow] = useState(false);
    const location = useLocation();
    const passwordChangeRequired = useAuthStore(
        (s) => s.passwordChangeRequired || accessTokenRequiresPasswordChange(s.accessToken),
    );

    useEffect(() => {
        if (!rehydrating) return;
        const timer = setTimeout(() => setSlow(true), 4000);
        return () => clearTimeout(timer);
    }, [rehydrating]);

    useEffect(() => {
        if (accessToken && user) {
            setOk(true);
            setRehydrating(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setRehydrating(true);
                const token = accessToken ?? await refreshAccessToken();
                if (!token) throw new Error('no access token');

                const me = await apiClient.get<{
                    data: {
                        id: string;
                        email: string;
                        firstName: string | null;
                        lastName: string | null;
                        role: string;
                        features?: unknown;
                    };
                }>('/auth/me');

                if (cancelled) return;
                const currentUser = me.data.data;
                const hydratedUser = {
                    id: currentUser.id,
                    email: currentUser.email,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    role: currentUser.role as never,
                };
                useAuthStore.getState().setFeatures(parsePlatformFeatures(currentUser.features) ?? {});
                if (accessToken) {
                    setUser(hydratedUser);
                } else {
                    setSession({ accessToken: token, user: hydratedUser });
                }
                setOk(true);
            } catch {
                if (cancelled) return;
                reset();
                setOk(false);
            } finally {
                if (!cancelled) setRehydrating(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [accessToken, user, setSession, setUser, reset]);

    // After a fresh login the session comes from /auth/login (no `features`);
    // read /auth/me once in the background so disabled features stop polling.
    const featuresRequested = useRef(false);
    useEffect(() => {
        if (!ok || featuresRequested.current || useAuthStore.getState().features !== null) return;
        featuresRequested.current = true;
        apiClient
            .get<{ data: { features?: unknown } }>('/auth/me')
            .then((res) => useAuthStore.getState().setFeatures(parsePlatformFeatures(res.data?.data?.features) ?? {}))
            .catch(() => {
                // unknown features = treat as on; per-call PLATFORM_FEATURE_DISABLED still disables
            });
    }, [ok]);

    if (rehydrating) {
        return (
            <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-muted-foreground">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-tg-teal border-t-transparent" aria-label={t('common.loading')} />
                <div className="text-sm">
                    {slow
                        ? t('auth.loading.slow', { defaultValue: 'Starting the workspace. This can take up to a minute.' })
                        : t('auth.loading.initial', { defaultValue: 'Loading your console...' })}
                </div>
            </div>
        );
    }

    if (!ok) {
        return <Navigate to="/login" state={loginRedirectState(location, sessionEndReason)} replace />;
    }

    const changePasswordTarget = passwordChangeRedirect(location.pathname, passwordChangeRequired);
    if (changePasswordTarget) {
        return <Navigate to={changePasswordTarget} replace />;
    }

    return children;
}
